import React from 'react';
import './RideCompletedPage.css';
import { useLanguage } from './i18n/LanguageContext';
import { requestedVehicle, hasAssignedVendor, hasAssignedCar, hasAssignedDriver } from './utils/rides';

const RideCompletedPage = ({ ride, onBack, onContactSupport, onPay, needsPayment, variant = 'page' }) => {
  const { t } = useLanguage();
  if (!ride) {
    return null;
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch (e) {
      return dateString;
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (e) {
      return dateString;
    }
  };

  const amountOf = (...values) => {
    for (const value of values) {
      if (value !== undefined && value !== null && value !== '') {
        const number = parseFloat(value);
        if (!Number.isNaN(number)) return number;
      }
    }
    return null;
  };

  const finalFare = amountOf(ride.fare, ride.maxFare, ride.minFare, ride.grandTotal, ride.totalFare) || 0;
  const gstAmount = amountOf(ride.gstAmount, ride.gst) ?? Math.round(finalFare * 0.05);
  const parkingToll = amountOf(ride.parkingToll, ride.parkingCharges, ride.tollCharges) ?? Math.round(finalFare * 0.05);
  const serviceFee = amountOf(ride.serviceFee, ride.serviceCharge) ?? 0;
  const baseFare = amountOf(ride.baseFare) ?? (finalFare - gstAmount - parkingToll - serviceFee);
  const formatRupee = (amount) => `₹ ${Number(amount || 0).toLocaleString('en-IN')}`;
  const requested = requestedVehicle(ride);

  const handleContactSupport = () => {
    if (onContactSupport) {
      onContactSupport();
    } else {
      // Default behavior - navigate to contact page
      window.location.href = '/contact';
    }
  };

  return (
    <div className={`ride-completed-page${variant === 'panel' ? ' is-panel' : ''}`}>
      {/* Back Button */}
      <button className="completed-back-button" onClick={onBack}>
        <img
          src={process.env.PUBLIC_URL + '/images/back_button.svg'}
          alt="Back"
        />
      </button>

      {/* Completion Status */}
      <div className="completion-status">
        <div className="completion-icon">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        <h1 className="completion-title">{t('Ride completed')}</h1>
      </div>

      <div className="completed-ride-card">
        <div className="ride-detail-row">
          <div className="detail-dot pickup-dot"></div>
          <div className="ride-detail-content">
            <span className="detail-label">{t('PICKUP POINT')}</span>
            <span className="detail-value">{ride.startLocation || 'Pickup Location'}</span>
          </div>
        </div>
        <div className="ride-detail-row">
          <div className="detail-dot drop-dot"></div>
          <div className="ride-detail-content">
            <span className="detail-label">{t('DROP DESTINATION')}</span>
            <span className="detail-value">{ride.endLocation || 'Drop Location'}</span>
          </div>
        </div>
        <div className="completed-date-time">
          <span className="date-text">Date : {formatDate(ride.carRequestTime || ride.createdAt || ride.bookingDate || ride.completedAt)}</span>
          <span className="time-text">Time : {formatTime(ride.carRequestTime || ride.createdAt || ride.bookingDate || ride.completedAt || ride.pickupTime)}</span>
        </div>
        {requested ? (
          <p className="completed-requested">{t('Requested: {name}', { name: requested })}</p>
        ) : null}
        {hasAssignedVendor(ride) && ride.vendorName ? (
          <p className="completed-assigned">{ride.vendorName}</p>
        ) : null}
        {hasAssignedCar(ride) ? (
          <p className="completed-assigned">{[ride.carName, ride.carNumber].filter(Boolean).join(' • ')}</p>
        ) : null}
        {hasAssignedCar(ride) && hasAssignedDriver(ride) && ride.driverName ? (
          <p className="completed-assigned">{ride.driverName}{ride.driverMobileNumber ? ` • ${ride.driverMobileNumber}` : ''}</p>
        ) : null}
      </div>

      {/* Final Fare Card */}
      <div className="final-fare-card">
        <span className="fare-label">{t('FINAL FARE')}</span>
        <span className="fare-amount">{formatRupee(finalFare)}</span>
        {needsPayment ? (
          <span className="fare-status pending">{t('Payment Pending')}</span>
        ) : (
          <span className="fare-status">{t('Charged successfully')}</span>
        )}
      </div>

      {/* Fare Breakdown */}
      <div className="fare-breakdown">
        <div className="breakdown-row">
          <span className="breakdown-label">{t('Base fare')}</span>
          <span className="breakdown-value">{formatRupee(baseFare)}</span>
        </div>
        <div className="breakdown-row">
          <span className="breakdown-label">{t('GST (5%)')}</span>
          <span className="breakdown-value">{formatRupee(gstAmount)}</span>
        </div>
        <div className="breakdown-row">
          <span className="breakdown-label">{t('Parking & Toll Charges')}</span>
          <span className="breakdown-value">{formatRupee(parkingToll)}</span>
        </div>
        <div className="breakdown-row">
          <span className="breakdown-label">{t('Service fee')}</span>
          <span className="breakdown-value">{formatRupee(serviceFee)}</span>
        </div>
        <div className="breakdown-row grand-total">
          <span className="breakdown-label">{t('Grand total')}</span>
          <span className="breakdown-value">{formatRupee(finalFare)}</span>
        </div>
      </div>

      {/* Payment Button (if payment is pending) */}
      {needsPayment && onPay && (
        <button className="pay-button" onClick={onPay}>
          Pay {formatRupee(finalFare)}
        </button>
      )}

      {/* Contact Support Button */}
      <button className="contact-support-button" onClick={handleContactSupport}>
        Contact Support
      </button>
    </div>
  );
};

export default RideCompletedPage;
